
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpenCheck, Clock } from "lucide-react";
import { Student } from "@/services/db/types";
import ProgramItem from "./ProgramItem";
import { formatCurrency } from "@/utils/formatters";
import { differenceInDays } from 'date-fns';

export interface ProgramsCardProps {
  student: Student;
  calculateTotalFees: () => number;
}

const ProgramsCard = ({ student, calculateTotalFees }: ProgramsCardProps) => {
  // Calculate total remaining days across all programs
  const getTotalRemainingDays = () => {
    return student.programs.reduce((total, program) => {
      // Make sure to use the completion date, not the enrollment date
      const completionDate = new Date(program.completionDate);
      const today = new Date();
      const remainingDays = differenceInDays(completionDate, today);
      return total + (remainingDays > 0 ? remainingDays : 0);
    }, 0);
  };

  const totalRemainingDays = getTotalRemainingDays();
  
  return (
    <Card>
      <CardHeader className="bg-nalc-teal/10 dark:bg-nalc-teal/20">
        <CardTitle className="text-lg flex items-center">
          <BookOpenCheck className="h-5 w-5 mr-2" />
          Enrolled Programs
        </CardTitle>
        <CardDescription>
          All programs the student is currently enrolled in.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        {student.programs.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-muted-foreground">No programs found for this student.</p>
            <Link to={`/students/edit/${student.id}`}>
              <Button variant="link" className="mt-2">
                Add a program
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {student.programs.map((program, index) => (
              <ProgramItem
                key={index}
                program={program}
                student={student}
              />
            ))}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between border-t pt-6">
        <div className="text-sm text-muted-foreground flex items-center">
          <span>Total Programs: {student.programs.length}</span>
          {student.programs.length > 0 && (
            <span className="ml-4 flex items-center">
              <Clock className="h-4 w-4 mr-1 text-nalc-teal" />
              {totalRemainingDays > 0 ? `${totalRemainingDays} days remaining` : "All programs completed"}
            </span>
          )}
        </div>
        <div className="font-medium">
          Total Fees: {formatCurrency(calculateTotalFees())}
        </div>
      </CardFooter>
    </Card>
  );
};

export default ProgramsCard;
